import { useLazyQuery, useQuery } from '@apollo/client';
import {
  AlertTriangle,
  Banknote,
  Coins,
  CreditCard,
  Printer,
  Receipt,
  ShieldAlert,
  Stethoscope,
  TicketX,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CANCELLATIONS_RANGE_QUERY,
  CASH_DIFFERENCES_RANGE_QUERY,
  CASH_OUTS_RANGE_QUERY,
  DASHBOARD_SUMMARY_QUERY,
  DISCOUNTS_RANGE_QUERY,
  SALES_BY_CATEGORY_REPORT_QUERY,
  SALES_DAY_PAYMENT_METHOD_BREAKDOWN_QUERY,
  SALES_REPORT_CATEGORIES_QUERY,
  SALES_BY_VET_QUERY,
  SUSPICIOUS_MOVEMENTS_QUERY,
} from './dashboard.gql';
import type {
  CancellationRow,
  CashCutRow,
  CashOutRow,
  DashboardSummary,
  DiscountRow,
  SalesByCategoryReportResult,
  SalesReportCategoryOption,
  PaymentMethodBreakdownRow,
  SalesByVet,
  SuspiciousRow,
} from './types';
import {
  buildSalesReportPrintHtml,
  buildSalesReportTableView,
  formatSalesReportPeriodLabel,
  printHtmlInHiddenWindow,
  sortSalesReportRowsChronologically,
} from './sales-report-print';

import { useBranchScope } from '@/modules/auth/useAuth';
import { SUSPICIOUS_KIND_FROM_GQL } from '@/shared/graphql/enums';
import { formatCentsCompact, formatCentsString } from '@/shared/lib/money';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

interface SummaryResp {
  dashboardSummary: DashboardSummary;
}
interface VetResp {
  salesByVet: SalesByVet[];
}
interface CancResp {
  cancellationsRange: CancellationRow[];
}
interface DiscResp {
  discountsRange: DiscountRow[];
}
interface CashOutResp {
  cashOutsRange: CashOutRow[];
}
interface CashDiffResp {
  cashDifferencesRange: CashCutRow[];
}
interface SusResp {
  suspiciousMovements: SuspiciousRow[];
}
interface PaymentMethodBreakdownResp {
  salesDayPaymentMethodBreakdown: PaymentMethodBreakdownRow[];
}
interface SalesReportCategoriesResp {
  salesReportCategories: SalesReportCategoryOption[];
}
interface SalesByCategoryReportResp {
  salesByCategoryReport: SalesByCategoryReportResult;
}

type ReportTab =
  | 'sales_report'
  | 'sales_by_vet'
  | 'cancellations'
  | 'discounts'
  | 'cash_outs'
  | 'cash_differences'
  | 'suspicious';

const TABS: ReportTab[] = [
  'sales_report',
  'sales_by_vet',
  'cancellations',
  'discounts',
  'cash_outs',
  'cash_differences',
  'suspicious',
];

type SalesTemporality = 'today' | 'week' | 'month' | 'custom';

function startOfWeekISO(date: Date): string {
  const d = new Date(date);
  const day = d.getDay();
  const monday_delta = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + monday_delta);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function endOfWeekISO(date: Date): string {
  const d = new Date(date);
  const day = d.getDay();
  const sunday_delta = day === 0 ? 0 : 7 - day;
  d.setDate(d.getDate() + sunday_delta);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function todayISO(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString().slice(0, 10);
}

function firstOfMonthISO(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

export function DashboardPage(): JSX.Element {
  const { t } = useTranslation();
  const branch_id = useBranchScope();
  const [date, setDate] = useState<string>(todayISO());
  const [from_date, setFromDate] = useState<string>(firstOfMonthISO());
  const [to_date, setToDate] = useState<string>(todayISO());
  const [tab, setTab] = useState<ReportTab>('sales_report');
  const [show_sales_breakdown, setShowSalesBreakdown] = useState(false);
  const [sales_category_id, setSalesCategoryId] = useState('ALL');
  const [sales_temporality, setSalesTemporality] = useState<SalesTemporality>('today');
  const [custom_from_date, setCustomFromDate] = useState<string>(todayISO());
  const [custom_to_date, setCustomToDate] = useState<string>(todayISO());
  const [sales_report_period, setSalesReportPeriod] = useState<{ from: string; to: string } | null>(
    null,
  );
  const [show_sales_report_modal, setShowSalesReportModal] = useState(false);
  const [sales_report_error, setSalesReportError] = useState<string | null>(null);

  const summary_date = useMemo(() => new Date(`${date}T00:00:00`), [date]);
  const range_from = useMemo(() => new Date(`${from_date}T00:00:00`), [from_date]);
  const range_to = useMemo(() => new Date(`${to_date}T23:59:59.999`), [to_date]);

  const { data: summary_data } = useQuery<SummaryResp>(DASHBOARD_SUMMARY_QUERY, {
    variables: { date: summary_date, branch_id },
    fetchPolicy: 'cache-and-network',
    pollInterval: 60_000,
  });
  const { data: payment_breakdown_data, loading: payment_breakdown_loading } =
    useQuery<PaymentMethodBreakdownResp>(SALES_DAY_PAYMENT_METHOD_BREAKDOWN_QUERY, {
      variables: { date: summary_date, branch_id },
      fetchPolicy: 'cache-and-network',
      pollInterval: 60_000,
      skip: !show_sales_breakdown,
    });
  const { data: sales_categories_data } = useQuery<SalesReportCategoriesResp>(
    SALES_REPORT_CATEGORIES_QUERY,
    {
      fetchPolicy: 'cache-first',
    },
  );
  const [loadSalesByCategoryReport, { data: sales_report_data, loading: sales_report_loading }] =
    useLazyQuery<SalesByCategoryReportResp>(SALES_BY_CATEGORY_REPORT_QUERY, {
      fetchPolicy: 'network-only',
    });

  const summary = summary_data?.dashboardSummary;
  const sales_categories = sales_categories_data?.salesReportCategories ?? [];

  const onShowSalesReport = async (): Promise<void> => {
    setSalesReportError(null);
    const today = new Date();
    let selected_from = todayISO();
    let selected_to = todayISO();
    if (sales_temporality === 'week') {
      selected_from = startOfWeekISO(today);
      selected_to = endOfWeekISO(today);
    } else if (sales_temporality === 'month') {
      selected_from = firstOfMonthISO();
      selected_to = todayISO();
    } else if (sales_temporality === 'custom') {
      selected_from = custom_from_date;
      selected_to = custom_to_date;
    }

    const from = new Date(`${selected_from}T00:00:00`);
    const to = new Date(`${selected_to}T23:59:59.999`);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || from > to) {
      setSalesReportError(t('dashboard.sales_report_invalid_range'));
      return;
    }

    try {
      setSalesReportPeriod({ from: selected_from, to: selected_to });
      await loadSalesByCategoryReport({
        variables: {
          from,
          to,
          category_id: sales_category_id === 'ALL' ? null : sales_category_id,
        },
      });
      setShowSalesReportModal(true);
    } catch (err) {
      if (err instanceof Error) setSalesReportError(err.message);
      else setSalesReportError(t('dashboard.sales_report_load_error'));
    }
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">{t('dashboard.title')}</h2>
          <p className="text-muted-foreground text-sm">{t('dashboard.subtitle')}</p>
        </div>
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs">{t('dashboard.summary_date')}</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-9"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            className="h-9"
            onClick={() => setShowSalesBreakdown(true)}
          >
            {t('dashboard.sales_breakdown_open')}
          </Button>
        </div>
      </header>

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          icon={<TrendingUp className="h-4 w-4" />}
          title={t('dashboard.card_sales')}
          value={summary ? formatCentsString(summary.sales_total_cents) : '—'}
          hint={
            summary ? t('dashboard.sales_count_hint', { count: summary.sales_count }) : undefined
          }
        />
        <SummaryCard
          icon={<Banknote className="h-4 w-4" />}
          title={t('dashboard.card_paid')}
          value={summary ? formatCentsString(summary.paid_total_cents) : '—'}
        />
        <SummaryCard
          icon={<Wallet className="h-4 w-4" />}
          title={t('dashboard.card_open_accounts')}
          value={summary ? formatCentsString(summary.open_accounts_balance_cents) : '—'}
          hint={
            summary
              ? t('dashboard.open_accounts_hint', { count: summary.open_accounts_count })
              : undefined
          }
        />
        <SummaryCard
          icon={<TicketX className="h-4 w-4" />}
          title={t('dashboard.card_pending_tickets')}
          value={summary ? String(summary.pending_tickets_count) : '—'}
          hint={t('dashboard.card_pending_tickets_hint')}
        />
        <SummaryCard
          icon={<Stethoscope className="h-4 w-4" />}
          title={t('dashboard.card_occupied_consulting_rooms')}
          value={summary ? String(summary.occupied_consulting_rooms_count) : '—'}
        />
        <SummaryCard
          icon={<Users className="h-4 w-4" />}
          title={t('dashboard.card_waiting_patients')}
          value={summary ? String(summary.waiting_patients_count) : '—'}
        />
        <SummaryCard
          icon={<Coins className="h-4 w-4" />}
          title={t('dashboard.card_cash_in_month')}
          value={summary ? formatCentsString(summary.cash_in_total_cents) : '—'}
        />
        <SummaryCard
          icon={<Coins className="h-4 w-4" />}
          title={t('dashboard.card_cash_out_month')}
          value={summary ? formatCentsString(summary.cash_out_total_cents) : '—'}
        />
        <SummaryCard
          icon={<CreditCard className="h-4 w-4" />}
          title={t('dashboard.card_open_shifts')}
          value={summary ? String(summary.open_shifts_count) : '—'}
        />
        <SummaryCard
          icon={<AlertTriangle className="h-4 w-4" />}
          title={t('dashboard.card_cancellations')}
          value={summary ? String(summary.cancelled_sales_count) : '—'}
          hint={summary ? formatCentsString(summary.cancelled_sales_total_cents) : undefined}
        />
      </section>

      <section className="space-y-3">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">{t('dashboard.reports_title')}</h3>
            <p className="text-muted-foreground text-xs">{t('dashboard.reports_subtitle')}</p>
          </div>
          {tab !== 'sales_report' ? (
            <div className="flex items-end gap-2">
              <div className="space-y-1">
                <Label className="text-xs">{t('dashboard.range_from')}</Label>
                <Input
                  type="date"
                  value={from_date}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('dashboard.range_to')}</Label>
                <Input
                  type="date"
                  value={to_date}
                  onChange={(e) => setToDate(e.target.value)}
                  className="h-9"
                />
              </div>
            </div>
          ) : null}
        </header>

        <nav className="border-border flex flex-wrap gap-1 border-b">
          {TABS.map((tab_key) => (
            <button
              key={tab_key}
              type="button"
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                tab === tab_key
                  ? 'border-primary text-foreground'
                  : 'text-muted-foreground hover:text-foreground border-transparent'
              }`}
              onClick={() => setTab(tab_key)}
            >
              {t(`dashboard.tab_${tab_key}`)}
            </button>
          ))}
        </nav>

        {tab === 'sales_by_vet' ? (
          <SalesByVetTable from_date={range_from} to_date={range_to} />
        ) : null}
        {tab === 'sales_report' ? (
          <SalesReportTab
            categories={sales_categories}
            sales_category_id={sales_category_id}
            sales_temporality={sales_temporality}
            custom_from_date={custom_from_date}
            custom_to_date={custom_to_date}
            sales_report_error={sales_report_error}
            sales_report_loading={sales_report_loading}
            on_category_change={setSalesCategoryId}
            on_temporality_change={setSalesTemporality}
            on_custom_from_change={setCustomFromDate}
            on_custom_to_change={setCustomToDate}
            on_show={() => {
              void onShowSalesReport();
            }}
          />
        ) : null}
        {tab === 'cancellations' ? (
          <CancellationsTable from_date={range_from} to_date={range_to} />
        ) : null}
        {tab === 'discounts' ? <DiscountsTable from_date={range_from} to_date={range_to} /> : null}
        {tab === 'cash_outs' ? <CashOutsTable from_date={range_from} to_date={range_to} /> : null}
        {tab === 'cash_differences' ? (
          <CashDifferencesTable from_date={range_from} to_date={range_to} />
        ) : null}
        {tab === 'suspicious' ? (
          <SuspiciousTable from_date={range_from} to_date={range_to} />
        ) : null}
      </section>

      {show_sales_breakdown ? (
        <PaymentMethodBreakdownDialog
          date={date}
          rows={payment_breakdown_data?.salesDayPaymentMethodBreakdown ?? []}
          loading={payment_breakdown_loading}
          on_close={() => setShowSalesBreakdown(false)}
        />
      ) : null}

      {show_sales_report_modal ? (
        <SalesByCategoryReportDialog
          data={sales_report_data?.salesByCategoryReport ?? null}
          loading={sales_report_loading}
          period_label={
            sales_report_period
              ? formatSalesReportPeriodLabel(sales_report_period.from, sales_report_period.to)
              : ''
          }
          on_download={async () => {
            const report = sales_report_data?.salesByCategoryReport ?? null;
            if (!report || !sales_report_period) return;
            const period_label = formatSalesReportPeriodLabel(
              sales_report_period.from,
              sales_report_period.to,
            );
            const html = buildSalesReportPrintHtml({
              report,
              period_label,
              strings: {
                title: t('dashboard.sales_report_print_title'),
                period_label: t('dashboard.sales_report_print_period'),
                ticket_label: t('dashboard.sales_report_print_ticket'),
                pet_label: t('dashboard.sales_report_print_pet'),
                totals_label: t('dashboard.sales_report_column_totals'),
                row_total_label: t('dashboard.sales_report_col_row_total'),
              },
            });
            await printHtmlInHiddenWindow(html);
          }}
          on_close={() => setShowSalesReportModal(false)}
        />
      ) : null}
    </div>
  );
}

interface SummaryCardProps {
  icon: JSX.Element;
  title: string;
  value: string;
  hint?: string;
}

function SummaryCard({ icon, title, value, hint }: SummaryCardProps): JSX.Element {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
          {icon}
          <span>{title}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="font-mono text-xl font-semibold">{value}</p>
        {hint ? <p className="text-muted-foreground mt-1 text-xs">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}

interface RangeProps {
  from_date: Date;
  to_date: Date;
}

interface SalesReportTabProps {
  categories: SalesReportCategoryOption[];
  sales_category_id: string;
  sales_temporality: SalesTemporality;
  custom_from_date: string;
  custom_to_date: string;
  sales_report_error: string | null;
  sales_report_loading: boolean;
  on_category_change: (value: string) => void;
  on_temporality_change: (value: SalesTemporality) => void;
  on_custom_from_change: (value: string) => void;
  on_custom_to_change: (value: string) => void;
  on_show: () => void;
}

function SalesReportTab({
  categories,
  sales_category_id,
  sales_temporality,
  custom_from_date,
  custom_to_date,
  sales_report_error,
  sales_report_loading,
  on_category_change,
  on_temporality_change,
  on_custom_from_change,
  on_custom_to_change,
  on_show,
}: SalesReportTabProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <div className="grid gap-3 md:grid-cols-3">
          <div className="space-y-1">
            <Label className="text-xs">{t('dashboard.sales_report_category')}</Label>
            <select
              value={sales_category_id}
              onChange={(e) => on_category_change(e.target.value)}
              className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
            >
              <option value="ALL">{t('dashboard.sales_report_all_categories')}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t('dashboard.sales_report_temporality')}</Label>
            <select
              value={sales_temporality}
              onChange={(e) => on_temporality_change(e.target.value as SalesTemporality)}
              className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
            >
              <option value="today">{t('dashboard.sales_report_today')}</option>
              <option value="week">{t('dashboard.sales_report_week')}</option>
              <option value="month">{t('dashboard.sales_report_month')}</option>
              <option value="custom">{t('dashboard.sales_report_custom')}</option>
            </select>
          </div>
          <div className="flex items-end">
            <Button
              type="button"
              onClick={on_show}
              disabled={sales_report_loading}
              className="h-9 w-full md:w-auto"
            >
              {sales_report_loading ? t('common.loading') : t('dashboard.sales_report_show')}
            </Button>
          </div>
        </div>

        {sales_temporality === 'custom' ? (
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs">{t('dashboard.range_from')}</Label>
              <Input
                type="date"
                value={custom_from_date}
                onChange={(e) => on_custom_from_change(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t('dashboard.range_to')}</Label>
              <Input
                type="date"
                value={custom_to_date}
                onChange={(e) => on_custom_to_change(e.target.value)}
                className="h-9"
              />
            </div>
          </div>
        ) : null}

        {sales_report_error ? (
          <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-3 text-sm">
            {sales_report_error}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function SalesByVetTable({ from_date, to_date }: RangeProps): JSX.Element {
  const { t } = useTranslation();
  const branch_id = useBranchScope();
  const { data } = useQuery<VetResp>(SALES_BY_VET_QUERY, {
    variables: { from: from_date, to: to_date, branch_id },
    fetchPolicy: 'cache-and-network',
  });
  const rows = data?.salesByVet ?? [];
  if (rows.length === 0) return <EmptyHint />;
  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-muted-foreground text-xs uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">{t('dashboard.col_vet')}</th>
              <th className="px-3 py-2 text-right">{t('dashboard.col_count')}</th>
              <th className="px-3 py-2 text-right">{t('dashboard.col_total')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.veterinarian_id ?? 'no-vet'} className="border-border border-t">
                <td className="px-3 py-2">{r.full_name ?? '—'}</td>
                <td className="px-3 py-2 text-right font-mono">{r.sales_count}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {formatCentsString(r.total_cents)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function CancellationsTable({ from_date, to_date }: RangeProps): JSX.Element {
  const { t } = useTranslation();
  const branch_id = useBranchScope();
  const { data } = useQuery<CancResp>(CANCELLATIONS_RANGE_QUERY, {
    variables: { from: from_date, to: to_date, branch_id },
    fetchPolicy: 'cache-and-network',
  });
  const rows = data?.cancellationsRange ?? [];
  if (rows.length === 0) return <EmptyHint />;
  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-muted-foreground text-xs uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">{t('dashboard.col_folio')}</th>
              <th className="px-3 py-2 text-left">{t('dashboard.col_when')}</th>
              <th className="px-3 py-2 text-left">{t('dashboard.col_reason')}</th>
              <th className="px-3 py-2 text-right">{t('dashboard.col_total')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-border border-t">
                <td className="px-3 py-2 font-mono">{r.folio_number ?? r.id.slice(0, 8)}</td>
                <td className="px-3 py-2">{new Date(r.cancelled_at).toLocaleString()}</td>
                <td className="px-3 py-2">{r.cancel_note ?? r.cancel_reason_id ?? '—'}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {formatCentsString(r.total_cents)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function DiscountsTable({ from_date, to_date }: RangeProps): JSX.Element {
  const { t } = useTranslation();
  const branch_id = useBranchScope();
  const { data } = useQuery<DiscResp>(DISCOUNTS_RANGE_QUERY, {
    variables: { from: from_date, to: to_date, branch_id },
    fetchPolicy: 'cache-and-network',
  });
  const rows = data?.discountsRange ?? [];
  if (rows.length === 0) return <EmptyHint />;
  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-muted-foreground text-xs uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">{t('dashboard.col_when')}</th>
              <th className="px-3 py-2 text-left">{t('dashboard.col_actor')}</th>
              <th className="px-3 py-2 text-left">{t('dashboard.col_concept')}</th>
              <th className="px-3 py-2 text-left">{t('dashboard.col_reason')}</th>
              <th className="px-3 py-2 text-right">{t('dashboard.col_amount')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-border border-t">
                <td className="px-3 py-2">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-3 py-2 font-mono text-xs">
                  {r.actor_user_id?.slice(0, 8) ?? '—'}
                </td>
                <td className="px-3 py-2">{discountConceptLabel(r.reference_kind)}</td>
                <td className="px-3 py-2">{r.reason ?? '—'}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {r.amount_cents ? formatCentsString(r.amount_cents) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function discountConceptLabel(reference_kind: string | null): string {
  if (reference_kind === 'sale_order') return 'Descuento a la cuenta';
  if (reference_kind === 'sale_order_item') return 'Descuento a línea';
  return 'Descuento';
}

function CashOutsTable({ from_date, to_date }: RangeProps): JSX.Element {
  const { t } = useTranslation();
  const branch_id = useBranchScope();
  const { data } = useQuery<CashOutResp>(CASH_OUTS_RANGE_QUERY, {
    variables: { from: from_date, to: to_date, branch_id },
    fetchPolicy: 'cache-and-network',
  });
  const rows = data?.cashOutsRange ?? [];
  if (rows.length === 0) return <EmptyHint />;
  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-muted-foreground text-xs uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">{t('dashboard.col_when')}</th>
              <th className="px-3 py-2 text-left">{t('dashboard.col_reason')}</th>
              <th className="px-3 py-2 text-right">{t('dashboard.col_amount')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-border border-t">
                <td className="px-3 py-2">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-3 py-2">{r.reason}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {formatCentsString(r.amount_cents)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function CashDifferencesTable({ from_date, to_date }: RangeProps): JSX.Element {
  const { t } = useTranslation();
  const branch_id = useBranchScope();
  const { data } = useQuery<CashDiffResp>(CASH_DIFFERENCES_RANGE_QUERY, {
    variables: { from: from_date, to: to_date, branch_id },
    fetchPolicy: 'cache-and-network',
  });
  const rows = data?.cashDifferencesRange ?? [];
  if (rows.length === 0) return <EmptyHint />;
  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-muted-foreground text-xs uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">{t('dashboard.col_when')}</th>
              <th className="px-3 py-2 text-right">{t('dashboard.col_expected')}</th>
              <th className="px-3 py-2 text-right">{t('dashboard.col_declared')}</th>
              <th className="px-3 py-2 text-right">{t('dashboard.col_difference')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-border border-t">
                <td className="px-3 py-2">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {formatCentsString(r.expected_cash_cents ?? '0')}
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  {formatCentsString(r.declared_cash_cents ?? '0')}
                </td>
                <td className="px-3 py-2 text-right font-mono text-amber-600 dark:text-amber-400">
                  {formatCentsString(r.difference_cents)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function SuspiciousTable({ from_date, to_date }: RangeProps): JSX.Element {
  const { t } = useTranslation();
  const branch_id = useBranchScope();
  const { data } = useQuery<SusResp>(SUSPICIOUS_MOVEMENTS_QUERY, {
    variables: { from: from_date, to: to_date, branch_id },
    fetchPolicy: 'cache-and-network',
  });
  const rows = (data?.suspiciousMovements ?? []).map((row) => ({
    ...row,
    kind: SUSPICIOUS_KIND_FROM_GQL[row.kind as keyof typeof SUSPICIOUS_KIND_FROM_GQL] ?? row.kind,
  }));
  if (rows.length === 0) return <EmptyHint />;
  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-muted-foreground text-xs uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">{t('dashboard.col_kind')}</th>
              <th className="px-3 py-2 text-left">{t('dashboard.col_when')}</th>
              <th className="px-3 py-2 text-left">{t('dashboard.col_reference')}</th>
              <th className="px-3 py-2 text-left">{t('dashboard.col_actor')}</th>
              <th className="px-3 py-2 text-right">{t('dashboard.col_amount')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.kind}-${r.reference_id}`} className="border-border border-t">
                <td className="px-3 py-2">
                  <Badge variant={kindBadgeTone(r.kind)}>
                    <ShieldAlert className="mr-1 h-3 w-3" aria-hidden />
                    {t(`dashboard.kind_${r.kind}`)}
                  </Badge>
                </td>
                <td className="px-3 py-2">{new Date(r.occurred_at).toLocaleString()}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.reference_id.slice(0, 8)}</td>
                <td className="px-3 py-2 font-mono text-xs">
                  {r.actor_user_id?.slice(0, 8) ?? '—'}
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  {r.amount_cents ? formatCentsString(r.amount_cents) : r.detail}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function kindBadgeTone(kind: string): 'destructive' | 'outline' | 'default' {
  if (kind === 'cash_difference') return 'destructive';
  if (kind === 'large_discount') return 'destructive';
  return 'outline';
}

function EmptyHint(): JSX.Element {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="text-muted-foreground p-6 text-center text-sm">
        <Receipt className="mx-auto mb-2 h-5 w-5 opacity-50" aria-hidden />
        {t('dashboard.empty_range')}
      </CardContent>
    </Card>
  );
}

interface SalesByCategoryReportDialogProps {
  data: SalesByCategoryReportResult | null;
  loading: boolean;
  period_label: string;
  on_download: () => Promise<void>;
  on_close: () => void;
}

function SalesByCategoryReportDialog({
  data,
  loading,
  period_label,
  on_download,
  on_close,
}: SalesByCategoryReportDialogProps): JSX.Element {
  const { t } = useTranslation();
  const summary = data?.summary ?? null;
  const report_view = useMemo(() => buildSalesReportTableView(data), [data]);
  const columns = report_view.visible_columns;
  const rows = useMemo(() => sortSalesReportRowsChronologically(data?.rows ?? []), [data?.rows]);
  const cell_lookup = report_view.cell_lookup;
  const column_totals = report_view.column_totals;
  const row_totals = report_view.row_totals;
  const rows_grand_total = report_view.grand_total;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur"
    >
      <Card className="border-border/70 h-[88vh] w-full max-w-[96vw] shadow-xl">
        <CardHeader className="border-border/60 border-b pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <CardTitle className="text-xl">{t('dashboard.sales_report_modal_title')}</CardTitle>
              <p className="text-muted-foreground text-xs">
                {t('dashboard.sales_report_modal_subtitle')}
                {period_label ? ` - ${period_label}` : ''}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              className="h-9"
              onClick={() => {
                void on_download();
              }}
              disabled={loading || rows.length === 0}
            >
              <Printer className="mr-2 h-4 w-4" aria-hidden />
              {t('dashboard.sales_report_download_table')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex h-[calc(88vh-104px)] flex-col gap-4 pt-4">
          {loading ? (
            <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
          ) : (
            <>
              <div className="grid gap-2 md:grid-cols-4">
                <SummaryCard
                  icon={<TrendingUp className="h-4 w-4" />}
                  title={t('dashboard.sales_report_total_cents')}
                  value={summary ? formatCentsString(summary.total_cents) : '-'}
                />
                <SummaryCard
                  icon={<Receipt className="h-4 w-4" />}
                  title={t('dashboard.sales_report_tickets_count')}
                  value={summary ? String(summary.tickets_count) : '-'}
                />
                <SummaryCard
                  icon={<Wallet className="h-4 w-4" />}
                  title={t('dashboard.sales_report_avg_ticket')}
                  value={summary ? formatCentsString(summary.avg_ticket_cents) : '-'}
                />
                <SummaryCard
                  icon={<Coins className="h-4 w-4" />}
                  title={t('dashboard.sales_report_categories_total')}
                  value={summary ? formatCentsString(summary.categories_total_cents) : '-'}
                />
              </div>

              {rows.length === 0 ? (
                <p className="text-muted-foreground text-sm">{t('dashboard.empty_range')}</p>
              ) : (
                <div className="border-border/70 bg-card/30 min-h-0 flex-1 overflow-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/85 text-muted-foreground sticky top-0 z-10 text-xs uppercase tracking-wider backdrop-blur">
                      <tr>
                        <th className="px-3 py-2 text-left">{t('dashboard.col_when')}</th>
                        <th className="px-3 py-2 text-left">
                          {t('dashboard.sales_report_col_ticket_pet')}
                        </th>
                        {columns.map((col) => (
                          <th key={col.column_key} className="px-3 py-2 text-right">
                            {col.category_name}
                          </th>
                        ))}
                        <th className="text-foreground px-3 py-2 text-right">
                          {t('dashboard.sales_report_col_row_total')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => {
                        const row_cells =
                          cell_lookup.get(row.sale_order_id) ?? new Map<string, string>();
                        const ticket =
                          row.ticket_folio_number ??
                          row.sale_folio_number ??
                          row.sale_order_id.slice(0, 8);
                        const pet = row.pet_name ?? t('dashboard.sales_report_unknown_pet');
                        const pending_cents = safeBigIntString(row.pending_cents);
                        const is_abono = row.sale_status === 'abono';
                        const is_partial =
                          pending_cents > 0n &&
                          (row.sale_status === 'partially_paid' ||
                            row.sale_status === 'open_account');
                        return (
                          <tr
                            key={row.sale_order_id}
                            className={`border-border border-t ${index % 2 === 0 ? 'bg-background' : 'bg-muted/15'}`}
                          >
                            <td className="whitespace-nowrap px-3 py-2">
                              {new Date(row.paid_at).toLocaleString()}
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-medium">{ticket}</div>
                              <div className="text-muted-foreground text-xs">{pet}</div>
                              {is_abono ? (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  <Badge variant="secondary">{t('dashboard.badge_abono')}</Badge>
                                </div>
                              ) : null}
                              {is_partial ? (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  <Badge variant="outline">
                                    {t('dashboard.badge_partial_payment')}
                                  </Badge>
                                  <Badge variant="secondary">
                                    {t('dashboard.badge_open_account')}
                                  </Badge>
                                </div>
                              ) : null}
                              {is_partial ? (
                                <div className="text-muted-foreground mt-1 text-[11px]">
                                  {t('dashboard.partial_paid_short')}:{' '}
                                  <span className="font-mono">
                                    {formatCentsString(row.paid_cents)}
                                  </span>{' '}
                                  - {t('dashboard.partial_pending_short')}:{' '}
                                  <span className="font-mono">
                                    {formatCentsString(row.pending_cents)}
                                  </span>
                                </div>
                              ) : null}
                            </td>
                            {columns.map((col) => (
                              <td
                                key={`${row.sale_order_id}-${col.column_key}`}
                                className="px-3 py-2 text-right text-xs tabular-nums"
                              >
                                {formatCentsCompact(row_cells.get(col.column_key) ?? '0')}
                              </td>
                            ))}
                            <td className="text-foreground px-3 py-2 text-right text-xs font-semibold tabular-nums">
                              {formatCentsCompact(
                                (row_totals.get(row.sale_order_id) ?? 0n).toString(),
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-card/95 sticky bottom-0 z-10 backdrop-blur">
                      <tr className="border-primary/30 border-t-2">
                        <td className="text-muted-foreground px-3 py-2 text-xs font-semibold uppercase tracking-wide">
                          {t('dashboard.total_label')}
                        </td>
                        <td className="text-foreground px-3 py-2 text-sm font-semibold">
                          {t('dashboard.sales_report_column_totals')}
                        </td>
                        {columns.map((col) => (
                          <td
                            key={`totals-${col.column_key}`}
                            className="text-foreground px-3 py-2 text-right text-xs font-semibold tabular-nums"
                          >
                            {formatCentsCompact(
                              (column_totals.get(col.column_key) ?? 0n).toString(),
                            )}
                          </td>
                        ))}
                        <td className="text-foreground px-3 py-2 text-right text-sm font-bold tabular-nums">
                          {formatCentsCompact(rows_grand_total.toString())}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </>
          )}
          <div className="flex justify-end">
            <Button type="button" variant="ghost" onClick={on_close}>
              {t('common.close')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function safeBigIntString(value: string | null | undefined): bigint {
  try {
    if (!value || !/^-?\d+$/.test(value)) return 0n;
    return BigInt(value);
  } catch {
    return 0n;
  }
}

interface PaymentMethodBreakdownDialogProps {
  date: string;
  rows: PaymentMethodBreakdownRow[];
  loading: boolean;
  on_close: () => void;
}

function PaymentMethodBreakdownDialog({
  date,
  rows,
  loading,
  on_close,
}: PaymentMethodBreakdownDialogProps): JSX.Element {
  const { t } = useTranslation();
  const total_cents = rows.reduce<bigint>((acc, row) => acc + BigInt(row.total_cents || '0'), 0n);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur"
    >
      <Card className="w-full max-w-2xl">
        <CardHeader className="pb-2">
          <CardTitle>{t('dashboard.sales_breakdown_title')}</CardTitle>
          <p className="text-muted-foreground text-xs">
            {t('dashboard.sales_breakdown_subtitle', { date })}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
          ) : rows.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t('dashboard.empty_range')}</p>
          ) : (
            <div className="border-border overflow-hidden rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-muted-foreground text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2 text-left">{t('dashboard.col_payment_method')}</th>
                    <th className="px-3 py-2 text-right">{t('dashboard.col_count')}</th>
                    <th className="px-3 py-2 text-right">{t('dashboard.col_total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.payment_method_id} className="border-border border-t">
                      <td className="px-3 py-2">{row.payment_method_name}</td>
                      <td className="px-3 py-2 text-right font-mono">{row.payments_count}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {formatCentsString(row.total_cents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-border bg-muted/20 border-t">
                    <td className="px-3 py-2 font-semibold">{t('dashboard.total_label')}</td>
                    <td className="px-3 py-2" />
                    <td className="px-3 py-2 text-right font-mono font-semibold">
                      {formatCentsString(total_cents.toString())}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          <div className="flex justify-end">
            <Button type="button" variant="ghost" onClick={on_close}>
              {t('common.close')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
